import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { forwardRef, type MouseEvent, type Ref } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { Button } from '~/components/isolated/Button';
import { Counter, type CounterProps } from '~/components/isolated/Counter';
import { IconButton } from '~/components/isolated/IconButton';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';

import { userStore } from '~/stores';

import styles from '../BottomNavigationBarV2.module.scss';

// Types
export interface NavItem {
  to: string;
  icon: IconTypes;
  isActive: boolean;
}

export interface CTAButtonProps {
  text: string;
  subtitle?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: Nullable<IconTypes>;
  isActive?: boolean;
  isLoading?: boolean;
}

export interface ActionButtonProps {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export interface CartButtonProps {
  onClick?: () => void;
  count?: number;
  asLink?: boolean;
  to?: string;
  isInside?: boolean;
}

// Base Components
const BackButton = ({
  onClick,
  isBackOnly = false,
  isInside = false,
}: {
  onClick: () => void;
  isBackOnly?: boolean;
  isInside?: boolean;
}) => (
  <IconButton
    variant={isBackOnly ? 'default' : 'transparent'}
    size="XL"
    onClick={onClick}
    className={cn(
      styles.roundButton,
      isBackOnly && styles.dark,
      isInside && styles.isInside,
      styles.backButton,
      company.isMarketplaceVariant && styles.roundButtonMarketplace,
    )}
  >
    <Icon type="arrow" size={isInside ? 20 : 24} className="icon__rotate-180" />
  </IconButton>
);

const CartButton = forwardRef<HTMLElement, CartButtonProps>(
  ({ onClick, count, asLink, to = '/cart', isInside = false }, ref) => {
    const content = (
      <>
        <Icon type="navbar_cart" size={isInside ? 20 : 24} />
        {!!count && count > 0 && (
          <span className={styles.badge}>{count > 9 ? '9+' : count}</span>
        )}
      </>
    );

    if (asLink && to) {
      return (
        <Link
          to={to}
          className={cn(
            styles.roundButton,
            styles.primary,
            isInside && styles.isInside,
          )}
          ref={ref as Ref<HTMLAnchorElement>}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={`${styles.roundButton} ${styles.primary}`}
        onClick={onClick}
        ref={ref as Ref<HTMLButtonElement>}
      >
        {content}
      </button>
    );
  },
);

CartButton.displayName = 'CartButton';

const CTAButton = ({
  text,
  subtitle,
  onClick,
  disabled,
  icon,
  isActive,
  isLoading,
}: CTAButtonProps) => {
  const isLongText = text?.length > 15;

  return (
    <button
      className={`${styles.ctaButton} ${
        isActive ? styles.ctaButtonActive : ''
      }`}
      onClick={onClick}
      disabled={disabled || isLoading}
    >
      <div className={styles.ctaContent}>
        <div className={styles.ctaMain}>
          <>
            <span
              className={`${styles.ctaText} ${
                isLongText ? styles.ctaTextSmall : ''
              }`}
            >
              {text}
            </span>
            {icon && <Icon type={icon} size={24} />}
          </>
        </div>
        {isLoading ? (
          <span className={styles.ctaSpinner} />
        ) : (
          subtitle && <span className={styles.ctaSubtitle}>{subtitle}</span>
        )}
      </div>
    </button>
  );
};

interface NavigationGroupProps {
  items: NavItem[];
  cart?: Nullable<CartButtonProps & { ref?: React.RefObject<HTMLElement> }>;
  backButton?: { onClick: () => void; isBackOnly?: boolean };
}

const NavigationGroup = observer(
  ({ items, cart, backButton }: NavigationGroupProps) => {
    const { openModal } = useModal();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const handleAuthClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (!userStore.isAuthorized) {
        e.preventDefault();
        openModal(ModalType.Auth);
      }
    };

    const handleClickLink =
      (item: NavItem) => (e: MouseEvent<HTMLAnchorElement>) => {
        if (item.to === '/cabinet') {
          handleAuthClick(e);
          return;
        }

        if (item.to === '/') {
          if (pathname === item.to) {
            e.preventDefault();

            if (document.documentElement.scrollTop > 0) {
              document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
          return;
        }

        if (item.to === '/shop') {
          if (pathname === item.to) {
            e.preventDefault();

            navigate('/search');
          }
          return;
        }
      };

    return (
      <div
        className={`${styles.navGroup} ${
          company.isMarketplaceVariant ? styles.navGroupMarketplace : ''
        }`}
      >
        {backButton && <BackButton {...backButton} isInside />}
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`${styles.navItem} ${
              company.isMarketplaceVariant ? styles.navItemMarketplace : ''
            } ${item.isActive ? styles.navItemActive : ''}`}
            onClick={handleClickLink(item)}
          >
            <Icon
              type={`${item.icon}${item.isActive ? '-fill' : ''}` as IconTypes}
              size={24}
            />
          </Link>
        ))}
        {cart && <CartButton {...cart} ref={cart.ref} isInside />}
      </div>
    );
  },
);

const ActionButtons = ({
  primary,
  secondary,
}: NonNullable<NavigationBarProps['actions']>) => (
  <div className={styles.actionButtons}>
    <button
      className={styles.actionButtonSecondary}
      onClick={secondary.onClick}
    >
      {secondary.text}
    </button>
    <button className={styles.actionButtonPrimary} onClick={primary.onClick}>
      {primary.text}
    </button>
  </div>
);

const PrimaryActionButton = ({
  text,
  onClick,
  disabled,
  isLoading,
}: ActionButtonProps) => (
  <Button
    variant="primary"
    size="M"
    onClick={onClick}
    disabled={disabled}
    isLoading={isLoading}
    className={styles.primaryActionButton}
  >
    {text}
  </Button>
);

// Factory
export interface NavigationBarProps {
  variant:
    | 'default'
    | 'withBack'
    | 'withCTA'
    | 'withCounter'
    | 'backOnly'
    | 'backWithCart'
    | 'withActions'
    | 'backWithAction';
  backButton?: {
    onClick: () => void;
    isBackOnly?: boolean;
  };
  navigation?: {
    items: NavItem[];
  };
  cart?: Nullable<{
    count?: number;
    onClick?: () => void;
    asLink?: boolean;
    to?: string;
    ref?: React.RefObject<HTMLElement>;
  }>;
  counter?: Nullable<CounterProps>;
  cta?: Nullable<CTAButtonProps>;
  primaryAction?: ActionButtonProps;
  actions?: {
    primary: {
      text: string;
      onClick: () => void;
    };
    secondary: {
      text: string;
      onClick: () => void;
    };
  };
}

export const NavigationBarFabrique = observer(
  ({
    variant,
    backButton,
    navigation,
    cart,
    counter,
    cta,
    primaryAction,
    actions,
  }: NavigationBarProps) => {
    const renderContent = () => {
      switch (variant) {
        case 'default':
          return company.match({
            mcosmetic: navigation && (
              <NavigationGroup items={navigation.items} cart={cart} />
            ),
            default: (
              <>
                <div className={styles.spacer} />
                {navigation && <NavigationGroup items={navigation.items} />}
                {(!!cart && <CartButton {...cart} ref={cart.ref} />) || (
                  <div className={styles.spacer} />
                )}
              </>
            ),
          });

        case 'withBack':
          return company.match({
            mcosmetic: navigation && (
              <NavigationGroup
                items={navigation.items}
                cart={cart}
                backButton={backButton}
              />
            ),
            default: (
              <>
                {backButton && <BackButton onClick={backButton.onClick} />}
                {navigation && <NavigationGroup items={navigation.items} />}
                {(!!cart && <CartButton {...cart} ref={cart.ref} />) || (
                  <div className={styles.spacer} />
                )}
              </>
            ),
          });

        case 'withCTA':
          return (
            <>
              {backButton && <BackButton onClick={backButton.onClick} />}
              {cta && <CTAButton {...cta} />}
              {cart && <CartButton {...cart} ref={cart.ref} />}
            </>
          );
        case 'withCounter':
          return (
            <>
              {backButton && <BackButton onClick={backButton.onClick} />}
              {counter && <Counter {...counter} />}
              {cart && <CartButton {...cart} ref={cart.ref} />}
            </>
          );
        case 'backOnly':
          return (
            backButton && <BackButton isBackOnly onClick={backButton.onClick} />
          );
        case 'backWithCart':
          return (
            <>
              {backButton && (
                <BackButton isBackOnly onClick={backButton.onClick} />
              )}
              {cart && <CartButton {...cart} ref={cart.ref} />}
            </>
          );
        case 'withActions':
          return actions && <ActionButtons {...actions} />;

        case 'backWithAction':
          return (
            <>
              {backButton && <BackButton onClick={backButton.onClick} />}
              {primaryAction && <PrimaryActionButton {...primaryAction} />}
            </>
          );

        default:
          return null;
      }
    };

    return <div className={styles.contentContainer}>{renderContent()}</div>;
  },
);
